import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ArrowRight,
  CheckCircle2,
  CircleHelp,
  Download,
  ExternalLink,
  FileCheck2,
  Laptop,
  RefreshCw,
  Send,
  ShieldCheck,
  Smartphone,
  WalletCards,
  WifiOff,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

export default function HelpPage() {
  const { t } = useLanguage();
  const steps = [
    { number: "1", icon: Laptop, title: t("help.step1.title"), text: t("help.step1.text"), note: t("help.step1.note") },
    { number: "2", icon: Send, title: t("help.step2.title"), text: t("help.step2.text"), note: t("help.step2.note") },
    { number: "3", icon: Smartphone, title: t("help.step3.title"), text: t("help.step3.text"), note: t("help.step3.note") },
  ];
  const troubleshooting = [
    { icon: Download, title: t("help.trouble1.title"), text: t("help.trouble1.text") },
    { icon: RefreshCw, title: t("help.trouble2.title"), text: t("help.trouble2.text") },
    { icon: WifiOff, title: t("help.trouble3.title"), text: t("help.trouble3.text") },
    { icon: FileCheck2, title: t("help.trouble4.title"), text: t("help.trouble4.text") },
  ];
  return (
    <div className="space-y-8" data-testid="help-page">
      <div>
        <Badge variant="outline" className="mb-3 gap-1.5 text-primary border-primary/30 bg-primary/5">
          <CircleHelp size={12} /> {t("help.center")}
        </Badge>
        <h1 className="font-heading text-2xl md:text-3xl font-bold">{t("help.title")}</h1>
        <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
          {t("help.intro")}
        </p>
      </div>

      <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-primary/5 to-background p-5 md:p-6">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
          <div className="w-12 h-12 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-sm">
            <WalletCards size={24} />
          </div>
          <div className="flex-1">
            <p className="font-semibold">{t("help.deviceTitle")}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {t("help.deviceText")}
            </p>
          </div>
          <Link to="/dashboard/qrcode">
            <Button className="gap-2 w-full sm:w-auto" data-testid="help-open-qr-btn">
              {t("help.openQr")} <ArrowRight size={15} />
            </Button>
          </Link>
        </div>
      </div>

      <section>
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle2 size={18} className="text-emerald-500" />
          <h2 className="font-heading text-lg font-semibold">{t("help.stepsTitle")}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {steps.map((step) => (
            <Card key={step.number} className="relative overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <step.icon size={19} />
                  </div>
                  <span className="font-heading text-4xl font-bold text-primary/10">{step.number}</span>
                </div>
                <CardTitle className="text-base pt-2">{step.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.text}</p>
                <p className="text-xs text-foreground/70 mt-3 pt-3 border-t border-border/60">{step.note}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="grid lg:grid-cols-[1.35fr_0.65fr] gap-5">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("help.troubleshooting")}</CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-4">
            {troubleshooting.map((item) => (
              <div key={item.title} className="rounded-xl border border-border/70 bg-secondary/20 p-4">
                <item.icon size={18} className="text-primary mb-3" />
                <h3 className="text-sm font-semibold">{item.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed mt-2">{item.text}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><ShieldCheck size={18} className="text-emerald-500" /> {t("help.before")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {[
              t("help.req1"), t("help.req2"), t("help.req3"), t("help.req4"),
            ].map((item) => (
              <div key={item} className="flex gap-2.5 items-start">
                <CheckCircle2 size={15} className="text-emerald-500 mt-0.5 shrink-0" />
                <span className="text-muted-foreground">{item}</span>
              </div>
            ))}
            <div className="pt-3 mt-3 border-t border-border/70">
              <Link to="/dashboard/subscription" className="text-primary text-xs font-medium hover:underline inline-flex items-center gap-1">
                {t("help.checkPlan")} <ExternalLink size={11} />
              </Link>
            </div>
          </CardContent>
        </Card>
      </section>

      <section>
        <h2 className="font-heading text-lg font-semibold mb-3">{t("help.faq")}</h2>
        <Card>
          <CardContent className="pt-1">
            <Accordion type="single" collapsible>
              <AccordionItem value="real-pass">
                <AccordionTrigger>{t("help.faq1.q")}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {t("help.faq1.a")}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="updates">
                <AccordionTrigger>{t("help.faq2.q")}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {t("help.faq2.a")}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="share">
                <AccordionTrigger>{t("help.faq3.q")}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {t("help.faq3.a")}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="remove">
                <AccordionTrigger>{t("help.faq4.q")}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {t("help.faq4.a")}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      </section>

      <div className="rounded-2xl border border-border bg-secondary/20 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <p className="font-semibold">{t("help.still")}</p>
          <p className="text-sm text-muted-foreground mt-1">{t("help.stillText")}</p>
        </div>
        <Link to="/contact">
          <Button variant="outline" className="gap-2 w-full sm:w-auto"><CircleHelp size={15} /> {t("help.contact")}</Button>
        </Link>
      </div>
    </div>
  );
}
